import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Upload, ListChecks, Sparkles, Check, ArrowRight } from "lucide-react";
import { API, useAuth, getAuthHeaders } from "../App";

/**
 * Horizontal stepper shown on Dashboard for first-time users.
 * - Step 1: Upload a book / paste URL / paste text
 * - Step 2: Pick chapters (auto when book ready)
 * - Step 3: Generate a test, quiz, or notes
 * Auto-hides once user has completed a full cycle (book + at least one output).
 */
export default function OnboardingStepper() {
  const { token } = useAuth();
  const [progress, setProgress] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) return undefined;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/users/onboarding-progress`, {
          headers: getAuthHeaders(token),
        });
        if (!cancelled) setProgress(data);
      } catch (e) {
        // ignore — stepper will just stay hidden
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (!progress) return null;
  if (progress.completed && !dismissed) {
    // One-time success state, then hide
    setTimeout(() => setDismissed(true), 0);
  }
  if (progress.completed) return null;
  if (dismissed) return null;

  const hasBook = progress.has_book;
  const hasOutput = progress.has_output;

  const steps = [
    {
      id: 1,
      title: "Upload your book",
      desc: "Drop a PDF, paste a URL, or paste text",
      icon: Upload,
      done: hasBook,
      active: !hasBook,
      cta: { to: "/books/upload", label: "Start upload" },
      color: "indigo",
    },
    {
      id: 2,
      title: "Pick chapters",
      desc: "AI splits your book — choose what to cover",
      icon: ListChecks,
      done: hasBook && hasOutput,
      active: hasBook && !hasOutput,
      cta: hasBook && !hasOutput ? { to: "/books", label: "Open my books" } : null,
      color: "teal",
    },
    {
      id: 3,
      title: "Generate a test, quiz, or notes",
      desc: "Get your study material in seconds",
      icon: Sparkles,
      done: hasOutput,
      active: false,
      cta: null,
      color: "amber",
    },
  ];

  const completedCount = steps.filter((s) => s.done).length;
  const pct = Math.round((completedCount / steps.length) * 100);

  return (
    <div
      className="mb-6 sm:mb-8 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm"
      data-testid="onboarding-stepper"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">
            Getting started
          </p>
          <h3 className="text-lg sm:text-xl font-bold text-slate-900 mt-1">
            {pct === 0
              ? "3 steps to your first quiz"
              : `You're ${pct}% there — keep going`}
          </h3>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="text-xs text-slate-400 hover:text-slate-600 self-start sm:self-auto"
          data-testid="onboarding-dismiss"
        >
          Hide
        </button>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mb-6">
        <div
          className="h-full bg-gradient-to-r from-indigo-500 via-teal-500 to-amber-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Steps */}
      <div className="grid sm:grid-cols-3 gap-4">
        {steps.map((step) => {
          const Icon = step.icon;
          const colorMap = {
            indigo: { bg: "bg-indigo-100", text: "text-indigo-600", ring: "ring-indigo-500" },
            teal: { bg: "bg-teal-100", text: "text-teal-600", ring: "ring-teal-500" },
            amber: { bg: "bg-amber-100", text: "text-amber-600", ring: "ring-amber-500" },
          };
          const c = colorMap[step.color];
          return (
            <div
              key={step.id}
              className={`relative rounded-xl border p-4 transition-all ${
                step.done
                  ? "border-emerald-200 bg-emerald-50/40"
                  : step.active
                  ? `border-slate-200 bg-white ring-2 ${c.ring}/30`
                  : "border-slate-200 bg-slate-50/60"
              }`}
              data-testid={`onboarding-step-${step.id}`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                    step.done ? "bg-emerald-500" : c.bg
                  }`}
                >
                  {step.done ? (
                    <Check className="w-5 h-5 text-white" />
                  ) : (
                    <Icon className={`w-5 h-5 ${c.text}`} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-400">
                    Step {step.id}
                  </p>
                  <p className="font-semibold text-slate-900 text-sm sm:text-base leading-tight mt-0.5">
                    {step.title}
                  </p>
                  <p className="text-xs sm:text-sm text-slate-500 mt-1">
                    {step.desc}
                  </p>
                  {step.cta && (
                    <Link
                      to={step.cta.to}
                      className={`inline-flex items-center gap-1 mt-3 text-xs sm:text-sm font-semibold ${c.text} hover:underline`}
                      data-testid={`onboarding-cta-${step.id}`}
                    >
                      {step.cta.label}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
